import {AfterViewInit, ChangeDetectorRef, Component, ComponentRef, computed, DestroyRef, effect, inject, OnDestroy, OnInit, reflectComponentType, untracked, ViewContainerRef, viewChild, ChangeDetectionStrategy} from '@angular/core';
import {ActivatedRoute, NavigationEnd, Router, RouterOutlet} from '@angular/router';
import {Subject, Subscription} from 'rxjs';
import {RightSliderDrawer, RightSliderService} from 'src/app/services/ui/right-slider.service';
import {CurrentTenantStore} from "../../services/tenant/current-tenant.store";
import {MenuStateService} from "../../services/ui/menu-state.service";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {filter} from "rxjs/operators";
import {MatSidenavModule} from "@angular/material/sidenav";
import {IntercomService} from "../../services/ui/intercom.service";
import {KabeenAiService} from "../../services/chat/kabeen-ai.service";
import {LayoutStore} from "../../services/ui/layout.store";

import {OnboardingComponent} from 'src/app/modules/home/onboarding/onboarding.component';
import {MainMenuComponent} from 'src/app/modules/global/main-menu/main-menu.component';
import {TopbarComponent} from 'src/app/modules/home/topbar/topbar.component';
import {GlobalResearchShortcutService} from "../../services/search/global-research-shortcut.service";
import {ApplicationUrlService} from "../../services/applications/application-url.service";
import {Permission} from "src/app/services/auth/permissions.service";
import {RightDrawerRailComponent} from "src/app/modules/home/right-drawer-rail/right-drawer-rail.component";
import {PAGE_SCROLL_OPTIONS} from "src/app/utils/scrollbars.utils";
import {OverlayscrollbarsModule, OverlayScrollbarsComponent} from "overlayscrollbars-ngx";
import {redirectWhenSuspended} from "src/app/guards/suspended-tenant.guard";
import {ErrorStateComponent} from 'src/app/modules/design-system/error-state/error-state.component';
import {ConnectionStore} from 'src/app/services/ui/connection.store';
import type {PartialOptions} from "overlayscrollbars";

// How long the right drawer takes to leave the screen once it has been closed:
// the head start it gives the rail, then its own fold. Keep in sync with
// $drawer-close-delay + $drawer-close-duration in home.component.scss.
const DRAWER_EXIT_MS = 260;

@Component({
	templateUrl: './home.component.html',
	imports: [
		RouterOutlet,
		MatSidenavModule,
		OnboardingComponent,
		MainMenuComponent,
		TopbarComponent,
		RightDrawerRailComponent,
		OverlayscrollbarsModule,
		ErrorStateComponent
	],
	// The shell hosts the routed pages and the drawers, many of them default change detection with their own async state:
	// an OnPush shell would skip them.
	// eslint-disable-next-line @angular-eslint/prefer-on-push-component-change-detection
	changeDetection: ChangeDetectionStrategy.Eager,
	styleUrl: './home.component.scss',
})
export class HomeComponent implements OnInit, AfterViewInit, OnDestroy {
	rightSliderService = inject(RightSliderService);
	menuStateService = inject(MenuStateService);
	activatedRoute = inject(ActivatedRoute);
	private destroyRef = inject(DestroyRef);
	protected readonly tenantStore = inject(CurrentTenantStore);
	private kabeenAiService = inject(KabeenAiService);
	private intercomService = inject(IntercomService);
	private cdr = inject(ChangeDetectorRef);
	private router = inject(Router);

	/** False while a suspended workspace is sent away from a page it cannot open: the page is not rendered. */
	protected readonly pageOpen = redirectWhenSuspended();

	private readonly connection = inject(ConnectionStore);

	constructor() {
		this.rightSliderService.close();
		// Back from an outage: workspaces that failed to load load again, without waiting for "Réessayer".
		effect(() => {
			if (this.connection.issue() === 'restored' && untracked(this.tenantStore.loadError)) {
				untracked(() => this.tenantStore.retry());
			}
		});
	}

	readonly dynamicContainer = viewChild.required('dynamicContainer', {read: ViewContainerRef});

	// The drawer currently mounted, with everything that dies with it. All four are
	// set together in mountDrawer() and cleared together in destroyDrawer().
	private dynamicInstance: ComponentRef<any> | null = null;
	private drawerTeardown: Subscription | null = null;
	private closeResult: Subject<unknown> | null = null;
	private outputResult: Subject<unknown> | null = null;

	// Pending deferred teardown — see scheduleDrawerTeardown().
	private drawerExitTimer: ReturnType<typeof setTimeout> | null = null;


	private globalResearchShortcutService = inject(GlobalResearchShortcutService);
	protected readonly layout = inject(LayoutStore);
	protected applicationUrlService = inject(ApplicationUrlService);

	readonly pageScroll = viewChild<OverlayScrollbarsComponent>('pageScroll');

	readonly pageScrollOptions = PAGE_SCROLL_OPTIONS;
	/** Sous 960px : pas de défilement horizontal de page (tableaux et onglets défilent dans leur conteneur). */
	// Tactile : pas de pointeur qui « quitte » la page → les barres resteraient affichées
	// avec autoHide 'leave' ; elles s'effacent après le défilement, comme dans les drawers.
	readonly pageScrollOptionsHandset: PartialOptions = {
		...PAGE_SCROLL_OPTIONS,
		scrollbars: {...PAGE_SCROLL_OPTIONS.scrollbars, autoHide: 'scroll', autoHideDelay: 800},
		overflow: {x: 'hidden', y: 'scroll'},
	};

	private previousUrlPath: string | null = null;

	ngOnInit(): void {
		this.kabeenAiService.initialize();
		this.tenantStore.initialize();

		this.previousUrlPath = this.getUrlPath(this.router.url);
		this.router.events
			.pipe(filter(event => event instanceof NavigationEnd), takeUntilDestroyed(this.destroyRef))
			.subscribe((event: NavigationEnd) => this.handleNavigationScroll(event.urlAfterRedirects));
	}

	/**
	 * Resets the scroll of the main container when the user navigates to a different page.
	 * Query parameter changes (used for tabs, drawers, filters, ...) do not trigger a reset.
	 */
	private handleNavigationScroll(url: string): void {
		const newPath = this.getUrlPath(url);
		if (newPath !== this.previousUrlPath) {
			this.previousUrlPath = newPath;
			// The real scroller is the library's viewport, not the host element.
			this.pageScroll()?.osInstance()?.elements().viewport.scrollTo({top: 0, left: 0});
		}
	}

	private getUrlPath(url: string): string {
		return url.split('?')[0];
	}

	ngAfterViewInit(): void {
		this.intercomService.boot();
		this.rightSliderInit();
		this.cdr.detectChanges();
	}

	/**
	 * Backdrop and rail ask the drawer to close rather than closing it outright: a
	 * drawer exposing `onClose` decides what result it reports (a deleted flag, …)
	 * and may refuse. Drawers without one are closed straight through the service.
	 */
	protected requestDrawerClose(): void {
		const instance = this.dynamicInstance?.instance;
		if (instance?.onClose) {
			instance.onClose.emit();
		} else {
			this.closeResult?.next({});
			this.rightSliderService.close();
		}
	}

	rightSliderInit(): void {
		this.rightSliderService.onComponentChange()
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe(drawer => this.mountDrawer(drawer));

		// close() reaches us from the rail, the backdrop and any caller. The mounted
		// component has to go with it — the service holds no view of its own.
		this.rightSliderService.onCloseRequest()
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe(() => this.scheduleDrawerTeardown());
	}

	/**
	 * Drops the mounted drawer, but only once it has left the screen.
	 *
	 * Its content has to outlive close(): the drawer sits still for the rail to
	 * tuck back under it and only then folds away (home.component.scss), so a
	 * teardown on the spot would blank the card in full view of the user.
	 *
	 * Opening another drawer in the meantime cancels the wait and tears this one
	 * down at once — mountDrawer() goes through destroyDrawer() directly.
	 */
	private scheduleDrawerTeardown(): void {
		if (!this.dynamicInstance || this.drawerExitTimer !== null) {
			return;
		}
		this.drawerExitTimer = setTimeout(() => {
			this.drawerExitTimer = null;
			this.destroyDrawer();
		}, DRAWER_EXIT_MS);
	}

	private mountDrawer(drawer: RightSliderDrawer): void {
		this.destroyDrawer();

		this.dynamicInstance = this.dynamicContainer().createComponent(drawer.component);
		// A drawer taking `data` as an input (decorator or signal) gets it through setInput — assigning
		// a signal input would overwrite the signal; the others keep a plain field.
		if (reflectComponentType(drawer.component)?.inputs.some(input => input.propName === 'data')) {
			this.dynamicInstance.setInput('data', drawer.data);
		} else {
			this.dynamicInstance.instance.data = drawer.data;
		}

		this.closeResult = drawer.onClose;
		this.outputResult = drawer.onOutputEvent ?? null;

		// One bag per mount, dropped by destroyDrawer(): a stale subscription would
		// otherwise keep firing and tear down whichever drawer is on screen by then.
		const teardown = new Subscription();
		this.drawerTeardown = teardown;

		if (this.dynamicInstance.instance.onOutputEvent) {
			teardown.add(this.dynamicInstance.instance.onOutputEvent
				.subscribe((outputEvent: unknown) => drawer.onOutputEvent?.next(outputEvent)));
		}

		if (this.dynamicInstance.instance.onClose) {
			teardown.add(this.dynamicInstance.instance.onClose.subscribe((data: unknown) => {
				this.closeResult?.next(data);
				this.rightSliderService.close();
			}));
		}
	}

	/** Idempotent: safe to call when nothing is mounted, and safe to call twice. */
	private destroyDrawer(): void {
		this.cancelDrawerTeardown();

		this.drawerTeardown?.unsubscribe();
		this.drawerTeardown = null;

		this.closeResult?.complete();
		this.closeResult = null;

		this.outputResult?.complete();
		this.outputResult = null;

		this.dynamicInstance?.destroy();
		this.dynamicInstance = null;
	}

	/**
	 * The comments rail button only makes sense on a drawer that has a comments
	 * panel (that check lives in the slider service) and for a user allowed to see
	 * it — without the permission ApplicationDetailComponent forces `comments` back
	 * to false and strips the query param, so the button would look inert.
	 */
	protected readonly canNavigateToComment = computed(() =>
		this.rightSliderService.canNavigateToComment() && this.tenantStore.hasPermission(Permission.APPLICATIONS_COMMENT));

	protected toggleDrawerComments(): void {
		this.applicationUrlService.navigateInAndOutOfComments(!this.applicationUrlService.getCommentParameter());
	}

	/** True while the drawer shows one of its full-panel views (settings or comments) instead of its tabs. */
	protected get isDrawerViewOpen(): boolean {
		return this.applicationUrlService.getParameter() || this.applicationUrlService.getCommentParameter();
	}

	/** Rail back button on a full-panel view: back to the drawer's tabs, whichever view is open. */
	protected leaveDrawerView(): void {
		// Clears both `settings` and `comments` — the two views are exclusive.
		this.applicationUrlService.navigateInAndOutOfParameter(false);
	}

	protected get unreadCommentsNotifications(): number {
		return this.dynamicInstance?.instance?.unreadCommentsNotifications ?? 0;
	}

	protected get commentsCountNotifications(): number {
		return this.dynamicInstance?.instance?.commentsCount ?? 0;
	}

	ngOnDestroy(): void {
		this.cancelDrawerTeardown();
	}

	private cancelDrawerTeardown(): void {
		if (this.drawerExitTimer !== null) {
			clearTimeout(this.drawerExitTimer);
			this.drawerExitTimer = null;
		}
	}
}
